import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { useUserStore } from '../stores/useUserStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { productsService } from '../services/productsService';
import { Menu } from 'public-contracts';

const ModernMenuList: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, highestRole, userRoles } = useRBAC();
  const { user, fetchUser } = useUserStore();

  // Permission checks
  const canCreate = hasPermission('menus', 'create');
  const canEdit = hasPermission('menus', 'edit');
  const canDelete = hasPermission('menus', 'delete');

  // Fallback: Check if user is storeManager or above (for backward compatibility)
  const isStoreManagerOrAbove = user?.roles?.includes('storeManager') || 
                                 user?.roles?.includes('admin') || 
                                 user?.roles?.includes('orgAdmin') || 
                                 user?.roles?.includes('sysAdmin');
  
  // Use RBAC permission or fallback to role check
  const canCreateMenu = canCreate || isStoreManagerOrAbove || false;
  const canEditMenu = canEdit || isStoreManagerOrAbove || false;
  const canDeleteMenu = canDelete || isStoreManagerOrAbove || false;

  // Debug logging
  useEffect(() => {
    console.log('ModernMenuList - Permission Debug:', {
      user: user?.fullName,
      userRoles: user?.roles,
      highestRole,
      effectiveRoles: userRoles,
      canCreate,
      canEdit,
      canDelete,
      isStoreManagerOrAbove,
      canCreateMenu,
      canEditMenu,
      canDeleteMenu,
      hasPermissionMenusCreate: hasPermission('menus', 'create'),
      hasPermissionMenusEdit: hasPermission('menus', 'edit'),
      hasPermissionMenusDelete: hasPermission('menus', 'delete'),
    });
  }, [user, highestRole, userRoles, canCreate, canEdit, canDelete, canCreateMenu, canEditMenu, canDeleteMenu, isStoreManagerOrAbove, hasPermission]);

  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load real data - reload when location changes or pagination changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const menusData = await productsService.getMenus({
          page: pagination.page,
          perPage: pagination.perPage,
          ...(searchTerm && {
            filter: { name: { $regex: searchTerm, $options: 'i' } },
          }),
        });

        // Ensure we have an array
        const menusArray = Array.isArray(menusData)
          ? menusData
          : [];

        const total = (menusData as any).__total || menusArray.length;

        console.log('Menus data:', menusArray);
        console.log(`✅ Loaded ${menusArray.length} menus (page ${pagination.page}, total: ${total})`);

        setMenus(menusArray);
        setPagination((prev) => ({ ...prev, total }));
        setError(null); // Clear any previous errors
      } catch (error) {
        console.error('Error loading menu data:', error);
        setError(translate('pos.menus.load_error'));
        // Set empty array as fallback
        setMenus([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, pagination.page, pagination.perPage]);

  // Debounce search and reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      } else {
        // Trigger reload if already on page 1
        const loadData = async () => {
          try {
            setLoading(true);
            const menusData = await productsService.getMenus({
              page: 1,
              perPage: pagination.perPage,
              ...(searchTerm && {
                filter: { name: { $regex: searchTerm, $options: 'i' } },
              }),
            });

            const menusArray = Array.isArray(menusData)
              ? menusData
              : [];
            const total = (menusData as any).__total || menusArray.length;

            setMenus(menusArray);
            setPagination((prev) => ({ ...prev, total }));
          } catch (error) {
            console.error('Error loading menu data:', error);
            setMenus([]);
          } finally {
            setLoading(false);
          }
        };
        loadData();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, pagination.perPage]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const displayedMenus = menus;

  const handleCreateMenu = () => {
    navigate('/menus/create');
  };

  const handleEditMenu = (id: string) => {
    navigate(`/menus/${id}/edit`);
  };

  const handleDeleteMenu = async (id: string) => {
    if (
      window.confirm(
        translate('pos.menus.delete_confirm')
      )
    ) {
      try {
        await productsService.deleteMenu(id);
        setMenus(menus.filter((menu) => menu.id !== id));
      } catch (error) {
        console.error('Error deleting menu:', error);
        alert(translate('pos.menus.delete_error'));
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.menus.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {translate('pos.menus.title')}
          </h1>
          <p className="text-muted-foreground">
            {translate('pos.menus.subtitle')}
          </p>
        </div>
        {canCreateMenu && (
          <Button onClick={handleCreateMenu}>
            <Plus className="h-4 w-4 mr-2" />
            {translate('pos.menus.new_menu')}
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={translate(
                'pos.menus.search_placeholder'
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <div className="text-red-500">{error}</div>
          </CardContent>
        </Card>
      )}

      {/* Menus Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedMenus.map((menu) => (
          <Card key={menu.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{menu.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(canEditMenu || canDeleteMenu) && (
                <div className="flex gap-2 pt-2">
                  {canEditMenu && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditMenu(menu.id)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {translate('pos.common.action.edit')}
                    </Button>
                  )}
                  {canDeleteMenu && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteMenu(menu.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {displayedMenus.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            {searchTerm
              ? translate('pos.menus.no_results')
              : translate('pos.menus.no_menus')}
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('pos.orders.modern.pagination.showing', {
                start: Math.min(
                  (pagination.page - 1) * pagination.perPage + 1,
                  pagination.total
                ),
                end: Math.min(
                  pagination.page * pagination.perPage,
                  pagination.total
                ),
                total: pagination.total,
              })}{' '}
              {translate(
                'pos.menus.name',
                'menus'
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.previous',
                  'Previous'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page * pagination.perPage >= pagination.total
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.next',
                  'Next'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernMenuList;

